<template>
  <aside
    class="client-details-company-sidebar"
    :class="{ 'client-details-company-sidebar--records-page': variant === 'records-page' }"
    aria-label="Company records navigation"
  >
    <div class="client-details-company-sidebar-top">
      <div class="client-details-company-sidebar-brand">
        <h3 class="client-details-company-sidebar-title">{{ company.name || 'Company' }}</h3>
        <p v-if="company.legalName" class="client-details-company-sidebar-subtitle">
          {{ company.legalName }}
        </p>
      </div>
      <button
        v-if="showClose"
        type="button"
        class="client-details-company-sidebar-close"
        aria-label="Close company menu"
        @click="$emit('close')"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
    <p class="client-details-company-sidebar-intro">
      <template v-if="detailsSource === 'manager'">
        Open a manager page to view and update this company’s records (no client login required).
      </template>
      <template v-else>
        Go to this company’s dashboard to view or edit records.
      </template>
    </p>

    <nav v-if="detailsSource === 'manager'" class="client-details-company-sidebar-nav" aria-label="Manager company records">
      <div
        v-for="section in managerNavSections"
        :key="section.key"
        class="client-details-company-nav-accordion"
      >
        <button
          type="button"
          class="client-details-company-nav-accordion-toggle"
          :class="{
            'client-details-company-nav-accordion-toggle--open': accordionOpen[section.key],
            'client-details-company-nav-accordion-toggle--active-section': isManagerSectionActive(section)
          }"
          :aria-expanded="accordionOpen[section.key]"
          :aria-controls="`manager-nav-panel-${section.key}`"
          :id="`manager-nav-head-${section.key}`"
          @click="toggleAccordion(section.key)"
        >
          <span class="client-details-company-nav-accordion-label">{{ section.label }}</span>
          <i
            class="bi bi-chevron-down client-details-company-nav-accordion-chevron"
            :class="{ 'client-details-company-nav-accordion-chevron--open': accordionOpen[section.key] }"
            aria-hidden="true"
          />
        </button>
        <div
          :id="`manager-nav-panel-${section.key}`"
          class="client-details-company-nav-accordion-panel"
          role="region"
          :aria-labelledby="`manager-nav-head-${section.key}`"
          v-show="accordionOpen[section.key]"
        >
          <router-link
            class="client-details-company-sidebar-item client-details-company-sidebar-item--nested"
            active-class="active"
            :to="managerRoute(section.routeName)"
            @click="onNavClick"
          >
            <i :class="['bi', section.icon]"></i>
            <span>{{ section.linkText }}</span>
          </router-link>
        </div>
      </div>
    </nav>

    <nav v-else class="client-details-company-sidebar-nav" aria-label="Company shortcuts">
      <div
        v-for="section in adminNavSections"
        :key="section.key"
        class="client-details-company-nav-accordion"
      >
        <button
          type="button"
          class="client-details-company-nav-accordion-toggle"
          :class="{
            'client-details-company-nav-accordion-toggle--open': accordionOpen[section.key],
            'client-details-company-nav-accordion-toggle--active-section': isAdminSectionActive(section)
          }"
          :aria-expanded="accordionOpen[section.key]"
          :aria-controls="`admin-nav-panel-${section.key}`"
          :id="`admin-nav-head-${section.key}`"
          @click="toggleAccordion(section.key)"
        >
          <span class="client-details-company-nav-accordion-label">{{ section.label }}</span>
          <i
            class="bi bi-chevron-down client-details-company-nav-accordion-chevron"
            :class="{ 'client-details-company-nav-accordion-chevron--open': accordionOpen[section.key] }"
            aria-hidden="true"
          />
        </button>
        <div
          :id="`admin-nav-panel-${section.key}`"
          class="client-details-company-nav-accordion-panel"
          role="region"
          :aria-labelledby="`admin-nav-head-${section.key}`"
          v-show="accordionOpen[section.key]"
        >
          <router-link
            class="client-details-company-sidebar-item client-details-company-sidebar-item--nested"
            active-class="active"
            :to="dashboardPath(section.pathSuffix)"
            @click="onNavClick"
          >
            <i :class="['bi', section.icon]"></i>
            <span>{{ section.linkText }}</span>
          </router-link>
        </div>
      </div>
    </nav>
  </aside>
</template>

<script>
import './ClientCompanyRecordsNav.css';

const MANAGER_ROUTE_SECTION = {
  ManagerCompanyConsultants: 'consultants',
  ManagerCompanyTimesheets: 'consultants',
  ManagerCompanyCustomers: 'customers',
  ManagerCompanyInvoices: 'customers',
  ManagerCompanyVendors: 'vendors',
  ManagerCompanyBills: 'vendors'
};

export default {
  name: 'ClientCompanyRecordsNav',
  props: {
    detailsSource: {
      type: String,
      default: 'manager',
      validator: (v) => v === 'admin' || v === 'manager'
    },
    clientId: {
      type: [Number, String],
      required: true
    },
    company: {
      type: Object,
      required: true
    },
    showClose: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'inline',
      validator: (v) => v === 'inline' || v === 'records-page'
    }
  },
  emits: ['close', 'navigate'],
  data() {
    return {
      accordionOpen: {
        consultants: true,
        customers: false,
        vendors: false
      },
      managerNavSections: [
        {
          key: 'consultants',
          label: 'Consultants',
          routeName: 'ManagerCompanyTimesheets',
          icon: 'bi-calendar-week',
          linkText: 'Timesheets list'
        },
        {
          key: 'customers',
          label: 'Customers',
          routeName: 'ManagerCompanyInvoices',
          icon: 'bi-receipt',
          linkText: 'Invoices'
        },
        {
          key: 'vendors',
          label: 'Vendors',
          routeName: 'ManagerCompanyBills',
          icon: 'bi-file-earmark-text',
          linkText: 'Bills'
        }
      ],
      adminNavSections: [
        {
          key: 'consultants',
          label: 'Consultants',
          pathSuffix: 'consultant/hr-timesheets',
          icon: 'bi-calendar-week',
          linkText: 'Timesheets list'
        },
        {
          key: 'customers',
          label: 'Customers',
          pathSuffix: 'customers/invoices',
          icon: 'bi-receipt',
          linkText: 'Invoices'
        },
        {
          key: 'vendors',
          label: 'Vendors',
          pathSuffix: 'vendors/bills',
          icon: 'bi-file-earmark-text',
          linkText: 'Bills'
        }
      ]
    };
  },
  watch: {
    '$route.fullPath': {
      handler() {
        this.syncAccordionFromRoute();
      },
      immediate: true
    },
    detailsSource: {
      handler() {
        this.syncAccordionFromRoute();
      },
      immediate: true
    }
  },
  methods: {
    managerRoute(routeName) {
      return {
        name: routeName,
        params: {
          clientId: String(this.clientId),
          companyId: String(this.company.id)
        },
        query: { companyName: this.company.name || '' }
      };
    },
    dashboardPath(pathSuffix) {
      const id = this.company.id != null ? String(this.company.id) : '';
      return `/company/${id}/dashboard/${pathSuffix}`;
    },
    onNavClick() {
      this.$emit('navigate');
    },
    toggleAccordion(key) {
      this.accordionOpen[key] = !this.accordionOpen[key];
    },
    syncAccordionFromRoute() {
      if (this.detailsSource === 'manager') {
        const section = MANAGER_ROUTE_SECTION[this.$route.name];
        if (section) {
          this.accordionOpen = {
            consultants: section === 'consultants',
            customers: section === 'customers',
            vendors: section === 'vendors'
          };
        }
        return;
      }
      const p = this.$route.path || '';
      let key = 'consultants';
      if (p.includes('/customers/invoices')) key = 'customers';
      else if (p.includes('/vendors/bills')) key = 'vendors';
      else if (p.includes('/consultant/') || p.includes('/hr-timesheets')) key = 'consultants';
      this.accordionOpen = {
        consultants: key === 'consultants',
        customers: key === 'customers',
        vendors: key === 'vendors'
      };
    },
    isManagerSectionActive(section) {
      const routeName = this.$route.name;
      if (section.key === 'consultants') {
        return routeName === 'ManagerCompanyTimesheets' || routeName === 'ManagerCompanyConsultants';
      }
      if (section.key === 'customers') {
        return routeName === 'ManagerCompanyInvoices' || routeName === 'ManagerCompanyCustomers';
      }
      if (section.key === 'vendors') {
        return routeName === 'ManagerCompanyBills' || routeName === 'ManagerCompanyVendors';
      }
      return false;
    },
    isAdminSectionActive(section) {
      const p = this.$route.path || '';
      return p.includes(section.pathSuffix);
    }
  }
};
</script>
